import { Stack } from '@understory-io/leaf/stack';
import { Text } from '@understory-io/leaf/text';
import { css } from '@understory-io/leaf/css';
import { FormattedMessage } from 'react-intl';
import { TextLinkButton } from '~/ui/link';

type EmptyStateProps =
  | { variant?: 'empty'; onResetFilters?: never }
  | { variant: 'filtered'; onResetFilters: () => void };

/**
 * Rendered on the home page in two situations:
 *
 * - `'empty'` (default): the tenant has no listed experiences at all.
 * - `'filtered'`: experiences exist but the active filters return zero cards.
 *   Renders a "Reset filters" button that calls `onResetFilters`.
 *
 * The surrounding chrome (header / footer / private-event CTA) still renders —
 * this only replaces the experience grid + upcoming-dates section.
 */
export function EmptyState({ variant = 'empty', onResetFilters }: EmptyStateProps) {
  return (
    <Stack
      as="section"
      gap="small"
      align="center"
      className={css({
        textAlign: 'center',
        paddingY: { base: '3xlarge', tablet: '5xlarge' },
        // No Leaf token for a comfortable reading-width text column; use the
        // Panda escape hatch with a fixed pixel value so the heading + body
        // wrap before they reach the full PageCard width.
        maxWidth: '[600px]',
        marginX: 'auto',
      })}
    >
      {variant === 'filtered' ? (
        <>
          <Text as="h2" style="subheadline-large">
            <FormattedMessage
              defaultMessage="No experiences match your filters"
              description="Heading shown on the home page when active filters result in no experiences being displayed"
            />
          </Text>
          <Text as="p" style="body-large-regular" color="secondary">
            <FormattedMessage
              defaultMessage="Try adjusting or removing your filters to see more experiences."
              description="Body copy shown on the home page when active filters result in no experiences being displayed"
            />
          </Text>
          <TextLinkButton onClick={onResetFilters}>
            <FormattedMessage
              defaultMessage="Reset filters"
              description="Button label that clears all active filters on the home page"
            />
          </TextLinkButton>
        </>
      ) : (
        <>
          <Text as="h2" style="subheadline-large">
            <FormattedMessage
              defaultMessage="Not quite ready yet"
              description="Heading shown on the home page when the tenant has no listed experiences"
            />
          </Text>
          <Text as="p" style="body-large-regular" color="secondary">
            <FormattedMessage
              defaultMessage="There are no experiences available right now. Please check back soon!"
              description="Body copy shown on the home page when the tenant has no listed experiences"
            />
          </Text>
        </>
      )}
    </Stack>
  );
}
